/**
 * The evidence grade (task 63, decision 0041): how far an assessment's answers are backed, A to E, beside
 * the score wherever it is shown, with the reasons in words ("C: 2 answers contradicted by documents,
 * Insurance policy renewal passed 31 Oct 2025"). A score says how good the controls look; the grade says
 * how much of that is shown rather than said.
 *
 * It starts from the evidence confidence of the answers that earn credit (the score's layer 3: 0.2 when
 * everything is self-declared, 0.4 read from documents, 0.6 checked against them, more from connected
 * systems) and drops a grade for each kind of problem: answers the business's documents contradict (two
 * grades from three), many answers no document backs, and documents that have expired.
 */
export const EVIDENCE_GRADES = ['A', 'B', 'C', 'D', 'E'];
/** Lowest evidence confidence for each grade before problems are counted. */
export const GRADE_FLOOR = {
    A: 0.6,
    B: 0.4,
    C: 0.28,
    D: 0.01,
};
/** This many answers no document backs drops a grade. */
export const UNSUPPORTED_LIMIT = 3;
export const NO_EVIDENCE_FACTS = { contradicted: 0, unsupported: 0, expired: [] };
export const GRADE_TONE = {
    A: 'ok',
    B: 'ok',
    C: 'neutral',
    D: 'warn',
    E: 'bad',
};
const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;
const day = (iso) => new Date(`${iso}T00:00:00Z`).toLocaleDateString('en-MY', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' }); // prettier-ignore
export function evidenceGrade(confidence, facts = NO_EVIDENCE_FACTS) {
    const start = Object.keys(GRADE_FLOOR).find((g) => confidence >= GRADE_FLOOR[g]); // prettier-ignore
    let i = start ? EVIDENCE_GRADES.indexOf(start) : EVIDENCE_GRADES.length - 1;
    const pct = Math.round(confidence * 100);
    const reasons = [
        confidence <= 0
            ? 'No answers earning credit yet'
            : `${pct}% evidence confidence, ${confidence >= GRADE_FLOOR.A ? 'mostly checked or connected' : confidence >= GRADE_FLOOR.B ? 'much of it read from documents' : confidence >= GRADE_FLOOR.C ? 'some of it from documents' : 'mostly self-declared'}`,
    ]; // prettier-ignore
    const problems = [];
    if (facts.contradicted > 0) {
        i += facts.contradicted >= 3 ? 2 : 1;
        problems.push(`${plural(facts.contradicted, 'answer', 'answers')} contradicted by documents`);
    }
    if (facts.unsupported >= UNSUPPORTED_LIMIT)
        i += 1;
    if (facts.unsupported > 0)
        problems.push(`${plural(facts.unsupported, 'answer', 'answers')} not backed by documents`); // prettier-ignore
    if (facts.expired.length) {
        i += 1;
        for (const e of facts.expired)
            problems.push(`${e.title} passed ${day(e.due)}`);
    }
    const grade = EVIDENCE_GRADES[Math.min(i, EVIDENCE_GRADES.length - 1)];
    const shown = problems.length ? problems : reasons;
    return {
        grade,
        label: `${grade}: ${shown.slice(0, 3).join(', ')}${shown.length > 3 ? ` and ${shown.length - 3} more` : ''}`,
        reasons: [...reasons, ...problems],
    };
}
//# sourceMappingURL=evidence-grade.js.map